'use client';

import * as React from 'react';
import { ErrorBoundary } from '@/shared/ui/error-boundary';
import { ErrorState } from '@/shared/components/states/ErrorState';
import { useMeetingAnalytics } from '../hooks';
import { AnalyticsView } from '../components/AnalyticsView';
import { AnalyticsSkeleton } from '../components/AnalyticsSkeleton';

/**
 * Owns the analytics query and its states: skeleton while loading, an inline
 * ErrorState (never a toast) with retry on a failed GET — covering 401 / 403 /
 * 404 / 5xx / network / timeout via the shared error handler — and the view on
 * success, wrapped in an ErrorBoundary so a render fault degrades to a recovery
 * card instead of a blank page.
 */
export function AnalyticsContainer({ meetingId }: { meetingId: string }) {
  const query = useMeetingAnalytics(meetingId);

  return (
    <div className="mx-auto w-full max-w-3xl">
      {query.isPending ? (
        <AnalyticsSkeleton />
      ) : query.isError ? (
        <ErrorState
          error={query.error}
          onRetry={() => void query.refetch()}
          title="We couldn't load this meeting's analytics"
        />
      ) : query.data ? (
        <ErrorBoundary>
          <AnalyticsView data={query.data} />
        </ErrorBoundary>
      ) : null}
    </div>
  );
}
